import { useAbbiamoUiConfig } from '@seller-hermes/app/config';
import { store } from '@seller-hermes/app/globals';
import { ChangelogWidget } from '@seller-hermes/features/changelog/changelog-widget.component';
import {
  SellerGroupService,
  SellerGroupUser,
  SellerGroupUserPermissions,
} from '@seller-hermes/features/seller-group';
import SelectSellersDialog from '@seller-hermes/features/seller/SellersSelector/select-sellers-popup.component';
import SupportInfoPopup from '@seller-hermes/features/support-info-popup';
import { SecurityUserChip } from '@seller-hermes/features/mfa/SecurityUserChip';
import Avatar from '@seller-hermes/features/ui-v2/Avatar';
/* eslint-disable react/prop-types */
import { getBootstrappedContext } from '@seller-hermes/features/portal/context-bootstrap';
import { ContextSwitcher } from '@seller-hermes/features/portal/context-switcher';
import {
  buildLocalPortalDashboardUrl,
  getActiveSellerGroupId,
  getLocalPortalContextFromSearch,
  setActiveSellerGroupId,
} from '@seller-hermes/features/portal/portal-local-context';
import { getPortalRedirectTarget } from '@seller-hermes/features/portal/portal-redirects';
import { isMultiEnvContexts } from '@seller-hermes/features/portal/portal-multi-env';
import { PortalService } from '@seller-hermes/features/portal/portal.api';
import type {
  PortalProfile,
  PortalSellerGroupContext,
} from '@seller-hermes/features/portal/portal.types';
import { Badge } from '@seller-hermes/features/ui-v2/Badge';
import { Button } from '@seller-hermes/features/ui-v2/Button';
import Collapsible from '@seller-hermes/features/ui-v2/Collapsible';
import DropdownMenu from '@seller-hermes/features/ui-v2/DropdownMenu';
import DoubleSidebar from '@seller-hermes/features/ui-v2/double-sidebar';
import { FetchKeys } from '@seller-hermes/features/ui/constants';
import { UserTypeEnum } from '@seller-hermes/features/users/users.types';
import { useCanAccessWorkflows } from '@seller-hermes/features/workflows/hooks/useCanAccessWorkflows';
import {
  useStoreActions,
  useStoreState,
} from '@seller-hermes/hooks/easy-peasy';
import useSellerGroupFeatureFlag from '@seller-hermes/hooks/useSellerGroupFeatureFlag';
import {
  useRepassesAccess,
  useRepassesSettings,
} from '@seller-hermes/features/repasses/hooks/useRepassesQueries';
import { hasAsaasCredential } from '@seller-hermes/features/repasses/lib/kyc';
import { productTracker } from '@seller-hermes/product-tracking';
import { cn } from '@seller-hermes/utils/cn';
import { PROFILE_TYPES } from '@seller-hermes/utils/profileTypes';
import { isStandaloneLoginRuntime } from '@seller-hermes/utils/runtime';
import { addDays } from 'date-fns';
import {
  ArrowUpRight,
  BanknoteArrowDown,
  BookText,
  Box,
  CalendarClock,
  CarFront,
  ChartNoAxesCombined,
  ChevronRight,
  ChevronsLeftRightEllipsis,
  CircleCheck,
  CirclePercent,
  ClockAlert,
  Code,
  FileSymlink,
  FileText,
  Gauge,
  Globe,
  HelpCircle,
  House,
  IdCard,
  Inbox,
  LandPlot,
  LifeBuoy,
  ListRestart,
  ListTodo,
  LogOut,
  LucideIcon,
  Map,
  MapPin,
  Megaphone,
  MessageSquareText,
  Navigation2,
  PanelTop,
  Printer,
  Rss,
  ScrollText,
  Settings,
  Settings2,
  Sheet,
  ShieldCheck,
  Star,
  Store,
  SwatchBook,
  Tags,
  TextCursorInput,
  Triangle,
  TriangleAlert,
  Truck,
  TruckElectric,
  Users,
  Wallet,
  WandSparkles,
  Waypoints,
  Webhook,
  Zap,
  LayoutDashboard,
} from 'lucide-react';
import * as React from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import useLocalStorage from 'use-local-storage';
import { useIsMac } from '@seller-hermes/hooks/use-is-mac';

export {
  hasAnyPermission,
  hasHiredModules,
  hasHiredModulesPermission,
  hasProfile,
  hasProfilePermission,
} from '@seller-hermes/features/seller-group/user-access';
import {
  hasAnyPermission,
  hasHiredModules,
  hasHiredModulesPermission,
  hasProfile,
  hasProfilePermission,
} from '@seller-hermes/features/seller-group/user-access';

const getNavbarItems = (user: SellerGroupUser | null) => {
  const { t } = useTranslation('app-sidebar');
  const isPaymentsEnabled = useSellerGroupFeatureFlag('enable_payments_page');
  const isAnalyticsEnabled = useSellerGroupFeatureFlag('enable-analytics-page');
  const isStatusAliasesEnabled = useSellerGroupFeatureFlag('status_aliases');
  const isDashboardGenerativo = useSellerGroupFeatureFlag('dashboard-generativo');
  const isDriverOnboardingEnabled =
    useSellerGroupFeatureFlag('driver_onboarding');
  const isCteEmissionEnabled = useSellerGroupFeatureFlag('cte_emission');
  const isDceEmissionEnabled = useSellerGroupFeatureFlag('dce_emission');
  const isFiscalEnabled = isCteEmissionEnabled || isDceEmissionEnabled;
  const isExternalShippingTableEnabled = useSellerGroupFeatureFlag(
    'external_shipping_table',
  );
  const canShowWorkflows = useCanAccessWorkflows();
  // Menu: exclusão mútua pelo is_legacy_integration (decisão 2026-09-29).
  // SG migrado vê só o Carrier Hub (Tabelas de frete e Integrações antigas
  // somem); SG não migrado vê só as telas antigas, inclusive INTERNAL. O
  // acesso por URL do INTERNAL ao Hub (useCanAccessCarrierHub/rotas) segue
  // igual: é por onde o suporte acompanha a migração.
  const isHubMigrated = useStoreState(
    (s) => s.login.sellerGroupUser?.user?.seller_group?.is_legacy_integration,
  ) === false;

  if (!user) return {};

  // Quem vê a tela NOVA (staff Abbiamo OU marca com a flag). Decide também o
  // NOME da antiga: quem tem a nova vê a outra como "(antigo)"; quem só tem a
  // antiga a vê como "Dashboard" — pra ela não parecer a versão velha de algo
  // que a pessoa nem enxerga.
  const verNovoDashboard = user.type === 'INTERNAL' || isDashboardGenerativo;

  return {
    navCatchUp: {
      title: '',
      items: [
        {
          title: t('catch-up'),
          url: '/catch-up',
          icon: WandSparkles,
          show: true,
          items: [],
        },
      ],
    },
    navOperation: {
      title: t('operation'),
      items: [
        {
          // O Dashboard NOVO fica com o nome ("Dashboard") e a posição de cima.
          // LANÇAMENTO SOFT: staff Abbiamo (INTERNAL) OU marca com a flag
          // `dashboard-generativo` — espelha o guard da rota (`/dashboard`).
          // Quando sair do soft, troca os dois pra a permissão de PEDIDO.
          title: t('analytics'),
          url: '/dashboard',
          icon: LayoutDashboard,
          show: verNovoDashboard,
          items: [],
        },
        {
          // A tela ANTIGA (dashboard atual). Gate DELA inalterado: permissão de
          // dashboard + flag `enable-analytics-page` — mais o bypass de INTERNAL
          // (staff vê as duas). Nome vira "(antigo)" só pra quem TAMBÉM vê a
          // nova; pra quem só tem a antiga, ela É o "Dashboard".
          title: verNovoDashboard
            ? `${t('analytics')} (antigo)`
            : t('analytics'),
          url: '/analytics',
          icon: ChartNoAxesCombined,
          show:
            user.type === 'INTERNAL' ||
            (hasAnyPermission(user, [SellerGroupUserPermissions.DASHBOARD]) &&
              isAnalyticsEnabled),
          items: [],
        },
        {
          title: t('orders'),
          url: '/orders',
          icon: Box,
          show: hasAnyPermission(user, [SellerGroupUserPermissions.ORDERS]),
          items: [],
        },
        {
          title: t('routes'),
          url: '/routes',
          icon: Map,
          show: hasAnyPermission(user, [
            SellerGroupUserPermissions.ROUTES_TO_CARRIER,
            SellerGroupUserPermissions.ROUTES_TO_PRIVATE_FLEET,
          ]),
          items: [],
        },
        {
          title: 'Envios',
          url: '/deliveries',
          icon: TruckElectric,
          show: hasAnyPermission(user, [SellerGroupUserPermissions.DELIVERIES]),
          items: [],
        },
        {
          title: t('reports'),
          url: '/reports',
          icon: FileText,
          show: hasAnyPermission(user, [SellerGroupUserPermissions.REPORTS]),
          items: [],
        },
        {
          title: t('csat'),
          url: '/csat',
          icon: Star,
          show: hasAnyPermission(user, [SellerGroupUserPermissions.CSAT]),
          items: [],
        },
        {
          title: t('shippers'),
          url: '/shippers',
          icon: Truck,
          show: hasProfile(user, PROFILE_TYPES.CARRIER) && user.type !== 'USER',
          items: [],
        },
        {
          title: 'Mapa de Motoristas',
          url: '/driver-location',
          icon: Navigation2,
          show: hasProfile(user, PROFILE_TYPES.CARRIER),
          items: [],
        },
      ],
    },
    navCare: {
      title: 'Care',
      // Care disponível pra todos — sem flag de visibilidade nem plano.
      show: true,
      items: [
        {
          title: t('disputes'),
          url: '/care/disputes',
          icon: Inbox,
          show: true,
          items: [],
        },
        {
          title: 'Consumo',
          url: '/care/usage',
          icon: Gauge,
          show: true,
          items: [],
        },
        {
          title: 'Configurações',
          url: '/care/settings',
          icon: Settings,
          // Só MASTER (e INTERNAL) abrem as configurações do Care.
          show: user.type === 'MASTER' || user.type === 'INTERNAL',
          items: [],
        },
      ],
    },
    navRepasses: {
      title: 'Repasses',
      // Visibilidade real vem do brand_user (canSeeRepasses no render). O nome
      // "Pay" foi abolido dentro do Hermes → Repasses.
      show: true,
      items: [
        {
          title: 'Visão geral',
          url: '/repasses',
          icon: Gauge,
          show: true,
          items: [],
        },
        {
          title: 'Entregadores',
          url: '/repasses/drivers',
          icon: CarFront,
          show: true,
          items: [],
        },
        {
          title: 'Transferências',
          url: '/repasses/transfers',
          icon: BanknoteArrowDown,
          show: true,
          items: [],
        },
        {
          title: 'Saldo e recarga',
          url: '/repasses/balance',
          icon: Wallet,
          show: true,
          items: [],
        },
        {
          // Verificação (KYC) da conta da marca. `show` é resolvido no render
          // pela CREDENCIAL Asaas (a tela só existe pra quem tem conta lá).
          title: 'Verificação',
          url: '/repasses/kyc',
          icon: ShieldCheck,
          show: true,
          items: [],
        },
        {
          title: 'Configurações',
          url: '/repasses/settings',
          icon: Settings,
          show: true,
          items: [],
        },
      ],
    },
    // Auditoria (pay-into-hermes): segregado do Repasses. Repasses = motoristas;
    // Auditoria = transportadoras/contratos (auditoria de frete). Mesmo gate de
    // brand_user (canSeeRepasses no render).
    navAuditoria: {
      title: 'Auditoria',
      show: true,
      items: [
        {
          title: 'Transportadoras',
          url: '/auditoria/transportadoras',
          icon: Truck,
          show: true,
          items: [],
        },
        {
          title: 'Faturas',
          url: '/auditoria/faturas',
          icon: FileText,
          show: true,
          items: [],
        },
        {
          title: 'Contratos',
          url: '/auditoria/contratos',
          icon: ScrollText,
          show: true,
          items: [],
        },
        {
          title: 'Glossário de status',
          url: '/auditoria/glossario',
          icon: BookText,
          show: true,
          items: [],
        },
      ],
    },
    // Tickets marca ↔ Abbiamo (não é Care: Care = cliente final ↔ marca).
    // Liberado pra marca — o BFF filtra por conta. É a entrada de suporte
    // (o "?" também abre aqui; o modal antigo foi aposentado).
    navSupport: {
      title: 'Suporte',
      show: true,
      items: [
        {
          title: 'Tickets',
          url: '/support/tickets',
          icon: LifeBuoy,
          show: true,
          items: [],
        },
      ],
    },
    navEngage: {
      title: 'Engage',
      // Protótipo interno — só INTERNAL vê o menu por ora.
      show: user.type === 'INTERNAL',
      items: [
        {
          title: 'Campanhas',
          url: '/engage/campaigns',
          icon: Megaphone,
          show: true,
          items: [],
        },
      ],
    },
    navQuotation: {
      title: t('quotation'),
      items: [
        {
          title: t('quotations'),
          url: '/quotations',
          icon: CirclePercent,
          show: false,
          items: [],
        },
        {
          title: t('quotation-rules'),
          url: '/quotation-rules',
          icon: ListTodo,
          show: hasProfilePermission(user, [
            SellerGroupUserPermissions.QUOTATION_RULES,
          ]),
          items: [],
        },
      ],
    },
    navAudit: {
      title: 'Faturas',
      items: [
        {
          title: 'Faturas',
          url: '/payments',
          icon: BanknoteArrowDown,
          show:
            hasAnyPermission(user, [SellerGroupUserPermissions.PAYMENTS]) &&
            isPaymentsEnabled,
          items: [],
        },
      ],
    },
    navSetup: {
      title: t('setup'),
      items: [
        {
          title: t('sellers'),
          url: '/sellers',
          icon: Store,
          show: hasAnyPermission(user, [SellerGroupUserPermissions.SELLERS]),
          items: [],
        },
        {
          title: t('shipping-tables'),
          url: '/shipping-tables',
          icon: Sheet,
          show:
            !isHubMigrated &&
            hasAnyPermission(user, [SellerGroupUserPermissions.FREIGHT_TABLES]),
          items: [],
        },
        {
          // Tabelas de CEP externas (preço do CT-e fiscal): página própria,
          // independente das Tabelas de Frete que o Carrier Hub assume. Aparece
          // pra grupos migrados e não migrados (sem o gate !isHubMigrated).
          // Gate por flag `external_shipping_table` (admin), igual CT-e/DC-e.
          title: t('external-tables'),
          url: '/external-tables',
          icon: Globe,
          show:
            Boolean(isExternalShippingTableEnabled) &&
            hasAnyPermission(user, [
              SellerGroupUserPermissions.FREIGHT_TABLES,
            ]),
          items: [],
        },
        {
          title: t('order-integrations'),
          url: '/order-integrations',
          icon: FileSymlink,
          show: hasAnyPermission(user, [
            SellerGroupUserPermissions.ORDER_INTEGRATIONS,
          ]),
          items: [],
        },
        {
          // Exclusão mútua com o Hub (Fase 5.5): nunca os dois ao mesmo
          // tempo — is_legacy_integration decide qual tela o seller_group vê.
          title: t('carrier-integrations'),
          url: '/carrier-integrations',
          icon: ChevronsLeftRightEllipsis,
          show:
            !isHubMigrated &&
            hasAnyPermission(user, [
              SellerGroupUserPermissions.CARRIER_INTEGRATIONS,
            ]),
          items: [],
        },
        {
          // Carrier Hub — gate real (is_legacy_integration), decisão 0.4.
          // Só o SG migrado vê esta entrada; o legado segue com
          // "Integrações de transportadora" (t('carrier-integrations')).
          title: 'Hub de transportadoras',
          url: '/carrier-hub',
          icon: Waypoints,
          show: isHubMigrated,
          badge: (
            <Badge variant="destructive" className="ml-auto">
              Novo
            </Badge>
          ),
          items: [],
        },
        {
          title: t('tags-rules'),
          url: '/tags-rules',
          icon: Tags,
          show: hasAnyPermission(user, [SellerGroupUserPermissions.TAGS_RULE]),
          items: [],
        },
        {
          title: t('dispatch-rules'),
          url: '/dispatch-rules',
          icon: Zap,
          show: hasAnyPermission(user, [
            SellerGroupUserPermissions.DISPATCH_RULE_TO_CARRIER,
          ]),
          items: [],
        },
        {
          title: t('resend-rules'),
          url: '/resend-rules',
          icon: ListRestart,
          show: hasAnyPermission(user, [
            SellerGroupUserPermissions.RESEND_RULE_TO_CARRIER,
          ]),
          items: [],
        },
        {
          title: t('inactivity-rules'),
          url: '/inactivity-rules',
          icon: ClockAlert,
          show: hasAnyPermission(user, [
            SellerGroupUserPermissions.INACTIVITY_RULE_TO_CARRIER,
          ]),
          items: [],
        },
        {
          // Régua do meio da esteira: roda depois da automação de marcador e
          // antes dos tiers de risco — por isso fica entre as duas no menu.
          title: 'Automação de prazo prometido',
          url: '/promised-date-rules',
          icon: CalendarClock,
          // CRUD é INTERNAL/MASTER (proxy no dashboard-api); espelha o guard.
          show: user.type === 'MASTER' || user.type === 'INTERNAL',
          items: [],
        },
        {
          title: 'Tiers de risco de atraso',
          url: '/delay-risk-tiers',
          icon: TriangleAlert,
          // Leitura liberada pra MASTER/INTERNAL/ADMIN (USER de fora); o CRUD
          // no dashboard-api segue exigindo só MASTER/INTERNAL — os botões de
          // escrita na própria tela ficam desabilitados pra ADMIN.
          show:
            user.type === 'MASTER' ||
            user.type === 'INTERNAL' ||
            user.type === 'ADMIN',
          items: [],
        },
        {
          title: 'Workflows',
          url: '/workflows',
          icon: WandSparkles,
          show: canShowWorkflows,
          items: [],
        },
        {
          title: t('offer-automation-rules'),
          url: '/offer-automation-rules',
          icon: CarFront,
          show: hasAnyPermission(user, [
            SellerGroupUserPermissions.CROWDSHIPPING,
          ]),
          items: [],
        },
        {
          title: t('drivers'),
          url: '/drivers',
          icon: IdCard,
          show: hasAnyPermission(user, [SellerGroupUserPermissions.DRIVERS]),
          items: [],
        },
        // Irmãos de Motoristas, não filhos: como sub-item o renderizador nem
        // avalia o `show`, e o pai nasce colapsado — o item existia e não
        // aparecia até alguém clicar no chevron.
        //
        // Os dois gateiam por quem contratou GO, que é a pergunta certa e não
        // o `profile` do grupo. Marca de perfil LOG que contratou GO TEM frota
        // própria e cadastra motorista. Medido contra as 71 marcas com
        // motorista no último ano: por módulo contratado, 63 veem o menu; por
        // `profile`, só 26.
        {
          title: t('driver-onboarding'),
          url: '/drivers/onboarding',
          icon: CircleCheck,
          show:
            hasAnyPermission(user, [SellerGroupUserPermissions.DRIVERS]) &&
            hasHiredModules(user, ['GO']) &&
            isDriverOnboardingEnabled,
          items: [],
        },
        {
          title: t('driver-terms'),
          url: '/drivers/terms',
          icon: ScrollText,
          show:
            hasAnyPermission(user, [SellerGroupUserPermissions.DRIVERS]) &&
            hasHiredModules(user, ['GO']) &&
            isDriverOnboardingEnabled,
          items: [],
        },
      ],
    },
    navSettingsGeneral: {
      title: t('settings-general'),
      items: [
        {
          title: t('preferences'),
          url: '/settings/preferences',
          icon: Settings2,
          show: true,
          items: [],
        },
        {
          // Tela de usuários local matada por dessincronia com o portal
          // (dashboard e portal escreviam vínculo de filial sem invalidar o
          // cache um do outro). Gestão de usuários agora só existe no portal.
          title: t('users'),
          url: `${process.env['NX_PORTAL_URL'] ?? ''}/users`,
          icon: Users,
          external: true,
          show: hasAnyPermission(user, [SellerGroupUserPermissions.USERS]),
          items: [],
        },
        {
          title: t('themes'),
          url: '/settings/themes',
          icon: SwatchBook,
          show: hasAnyPermission(user, [SellerGroupUserPermissions.THEMES]),
          items: [],
        },
        {
          title: t('notifications'),
          url: '/settings/notifications',
          icon: Rss,
          show: hasAnyPermission(user, [
            SellerGroupUserPermissions.NOTIFICATIONS,
          ]),
          items: [],
        },
      ],
    },
    navSettingsDeveloper: {
      title: t('settings-developer'),
      items: [
        {
          title: t('api'),
          url: '/settings/tokens',
          icon: Code,
          show: hasAnyPermission(user, [SellerGroupUserPermissions.API]),
          items: [],
        },
        {
          title: t('webhooks'),
          url: '/settings/webhooks',
          icon: Webhook,
          show: hasAnyPermission(user, [SellerGroupUserPermissions.WEBHOOKS]),
          items: [],
        },
        {
          title: 'Logs',
          url: '/settings/logs',
          icon: ScrollText,
          show: hasAnyPermission(user, [SellerGroupUserPermissions.WEBHOOKS]),
          items: [],
        },
      ],
    },
    navSettingsOthers: {
      title: t('settings-others'),
      items: [
        {
          title: t('tags'),
          url: '/settings/tags',
          icon: Tags,
          show: hasAnyPermission(user, [SellerGroupUserPermissions.TAGS_RULE]),
        },
        {
          title: t('operation'),
          url: '/settings/operation',
          icon: LandPlot,
          show: true,
          items: [],
        },
        {
          title: t('fiscal'),
          url: '/settings/fiscal',
          icon: FileText,
          show: Boolean(isFiscalEnabled),
          items: [],
        },
        {
          title: t('status-aliases'),
          url: '/settings/status-aliases',
          icon: TextCursorInput,
          show:
            Boolean(isStatusAliasesEnabled) &&
            (user.type === 'MASTER' || user.type === 'INTERNAL'),
          items: [],
        },
        {
          title: t('tracking'),
          url: '/settings/tracking',
          icon: MapPin,
          show: true,
          disabled: true,
          badge: (
            <Badge variant="ghost" className="ml-auto">
              Em breve
            </Badge>
          ),
          items: [],
        },
        {
          title: t('mail-label'),
          url: '/settings/mail-label',
          icon: Printer,
          show: true,
          disabled: true,
          badge: (
            <Badge variant="ghost" className="ml-auto">
              Em breve
            </Badge>
          ),
          items: [],
        },
      ],
    },
  };
};

export function AppSidebar({
  className,
  ...props
}: React.ComponentProps<typeof DoubleSidebar>) {
  const { sellerTheme } = useTheme();

  const navigate = useNavigate();
  const location = useLocation();

  const { pathname } = window.location;
  const isSettingsPage = pathname.startsWith('/settings');

  const [openSelectSellersDialog, setOpenSelectSellersDialog] =
    React.useState(false);
  const isMac = useIsMac();

  // Atalho global pra abrir o seletor de filial (Ctrl/Cmd + B). Não dispara
  // enquanto o foco está num input/textarea (enableOnFormTags default = false),
  // pra não atrapalhar quem está digitando.
  useHotkeys(
    ['ctrl+b', 'meta+b'],
    () => setOpenSelectSellersDialog(true),
    { preventDefault: true },
  );

  const {
    navbar: {
      selectedSellers,
      setSelectedSellers,
      useAllSellers,
      setUseAllSellers,
      signoff,
    },
  } = useAbbiamoUiConfig();

  const { data: me, isLoading: isLoadingMe } = useQuery(
    [FetchKeys.ME],
    () => SellerGroupService.getSellerGroupUser(),
    {
      refetchOnWindowFocus: false,
      onSuccess(data) {
        if (selectedSellers.length === 0) {
          setSelectedSellers(data.sellers);
        }
      },
    },
  );

  const { data: portalMe } = useQuery(
    ['portal-me'],
    () => PortalService.getMe(),
    {
      enabled: Boolean(me),
      initialData: getBootstrappedContext()?.portalMe,
      // 🚨 O `staleTime` NÃO é afinação de cache — é o que faz o
      // `initialData` acima valer alguma coisa. Sem ele o padrão do
      // react-query é 0: o dado do bootstrap entra JÁ VENCIDO e a query
      // refaz o `/portal/me` na montagem, desperdiçando a busca que o
      // bootstrap acabou de fazer. Media na tela de Pedidos do São João:
      // o 2º `/portal/me` saía 73 ms depois do `/me` responder (por causa
      // do `enabled` acima) e custava 3.468 ms, disputando conexão com a
      // rajada que carrega a tabela.
      //
      // `Infinity` porque a resposta lista a quais grupos o usuário tem
      // acesso — muda por mudança de permissão, não durante a navegação; e
      // o bootstrap já busca uma versão fresca a cada carga de página.
      staleTime: Infinity,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const isPaymentsEnabled = useSellerGroupFeatureFlag('enable_payments_page');

  const sellers = React.useMemo(() => me?.sellers || [], [me]);
  const selectedSellersIds = useStoreState(
    (state) => state.login.sellerGroupUser.selectedSellersIds,
  );

  const selectedSellersForDialog = React.useMemo(() => {
    if (selectedSellersIds.length === 0) {
      return sellers;
    }
    return sellers.filter((seller) => selectedSellersIds.includes(seller.id));
  }, [selectedSellersIds, sellers]);

  const sellersIDontHaveAccess = React.useMemo(() => {
    return selectedSellers.filter(
      (seller) => !sellers.find((s) => s.id === seller.id),
    );
  }, [selectedSellers, sellers]);

  const user = store.getState().login.sellerGroupUser.user;
  const bootstrappedContext = getBootstrappedContext();
  const localPortalContext = React.useMemo(
    () => getLocalPortalContextFromSearch(location.search),
    [location.search],
  );
  const activeProfile = React.useMemo<PortalProfile>(() => {
    if (bootstrappedContext?.product) return bootstrappedContext.product;

    // Local ?product= is the supported replacement for product subdomains.
    if (localPortalContext?.product) return localPortalContext.product;
    const profileModuleName = user?.seller_group?.profile_module?.name;
    if (profileModuleName === 'GO' || profileModuleName === 'PAY') {
      return profileModuleName;
    }
    return 'LOG';
  }, [
    bootstrappedContext?.product,
    localPortalContext?.product,
    user?.seller_group?.profile_module?.name,
  ]);
  const activeSellerGroupId =
    bootstrappedContext?.sellerGroup.id ||
    getActiveSellerGroupId() ||
    user?.seller_group?.id ||
    null;
  const activePortalContext = React.useMemo(
    () =>
      portalMe?.seller_groups.find(
        (context) =>
          context.id === activeSellerGroupId &&
          context.profile === activeProfile,
      ),
    [activeProfile, activeSellerGroupId, portalMe?.seller_groups],
  );
  const isHomologation =
    bootstrappedContext?.isHomologation ??
    Boolean(activePortalContext?.is_homologation);

  // Seletor individual de contexto (em vez do toggle LOG/GO + chave HML)
  // quando alguma combinação perfil × ambiente se repete. Regra e motivo em
  // `portal-multi-env.ts`.
  const isMultiEnv = React.useMemo(
    () => isMultiEnvContexts(portalMe?.seller_groups),
    [portalMe?.seller_groups],
  );

  const portalContexts = React.useMemo(() => {
    const toOption = (context: PortalSellerGroupContext) => ({
      id: context.id,
      profile: context.profile,
      status: context.status || 'available',
      name: context.name || '',
      isHomologation: context.is_homologation,
      accountName:
        portalMe?.account?.name || context.name || me?.seller_group?.name || '',
    });

    if (isMultiEnv) {
      return (portalMe?.seller_groups || [])
        .filter(
          (context) =>
            context.status !== 'unavailable' && context.profile !== 'PAY',
        )
        .map(toOption);
    }

    return (portalMe?.seller_groups || [])
      .filter(
        (context) =>
          context.is_homologation === isHomologation &&
          context.profile !== 'PAY',
      )
      .map(toOption)
      .filter(
        (context, index, allContexts) =>
          allContexts.findIndex((item) => item.profile === context.profile) ===
          index,
      );
  }, [
    isHomologation,
    isMultiEnv,
    me?.seller_group?.name,
    portalMe?.account?.name,
    portalMe?.seller_groups,
  ]);
  const hasHomologationContext = React.useMemo(
    () =>
      !isMultiEnv &&
      Boolean(
        portalMe?.seller_groups.some(
          (context) =>
            context.profile === activeProfile && context.is_homologation,
        ),
      ),
    [activeProfile, isMultiEnv, portalMe?.seller_groups],
  );
  const navigateToPortalContext = React.useCallback(
    (nextContext: PortalSellerGroupContext | undefined) => {
      if (!nextContext || nextContext.status === 'unavailable') return;
      // Runtime standalone troca de contexto na própria origem (query string),
      // em vez de saltar pro domínio do produto — o preview não tem sessão
      // compartilhada com `sandbox.log.abbiamolog.com`.
      const isStandaloneRuntime = isStandaloneLoginRuntime();
      const target = getPortalRedirectTarget(
        nextContext,
        isStandaloneRuntime ? 'local' : 'production',
      );

      if (!target.isAvailable) return;

      setActiveSellerGroupId(nextContext.id, nextContext.profile);
      // TOGGLE de ambiente: preserva a página atual (ex.: trocar homolog no
      // /dashboard continua no /dashboard). Só aqui passamos o pathname — o
      // seletor de contexto (login-context.page) usa o default `/` pra não
      // navegar pra si mesmo em loop.
      window.location.href = isStandaloneRuntime
        ? buildLocalPortalDashboardUrl(nextContext, window.location.pathname)
        : target.url;
    },
    [],
  );

  const handleSelectSellers = React.useCallback(
    (nextSellers: SellerGroupUser['sellers']) => {
      if (!useAllSellers) {
        setSelectedSellers(nextSellers);
      }
    },
    [setSelectedSellers, useAllSellers],
  );

  const data = getNavbarItems(user);

  // SEGURANÇA (pay-into-hermes): o submenu Repasses só aparece pra quem é
  // brand_user (pay.brand_users), decidido no pay-api. Fail-closed: qualquer
  // erro/carregando → escondido. Ver docs/pay-into-hermes.md §4.2.
  const repassesAccess = useRepassesAccess();
  const isBrandUser = repassesAccess.data?.has_pay === true;
  const repassesFlags = repassesAccess.data?.flags ?? null;
  // Gate por PERFIL do seller group: Repasses só em contexto GO; Auditoria só
  // em contexto LOG (`activeProfile` = perfil do contexto ativo no switcher).
  // Combinado com brand_user + a feature flag de cada área.
  const isGoProfile = activeProfile === 'GO';
  const isLogProfile = activeProfile === 'LOG';
  // Cada submenu é gateado por: brand_user + perfil do contexto + feature flag
  // (espelha os @RequireFlag do pay-api). Repasses→GO+drivers;
  // Auditoria→LOG+carriers/invoices/contracts. Faltando qualquer um, some.
  const canSeeRepasses =
    isBrandUser && isGoProfile && repassesFlags?.drivers === true;
  const canSeeCarriers =
    isBrandUser && isLogProfile && repassesFlags?.carriers === true;
  const canSeeInvoices =
    isBrandUser && isLogProfile && repassesFlags?.invoices === true;
  const canSeeContracts =
    isBrandUser && isLogProfile && repassesFlags?.contracts === true;
  const canSeeAuditoria = canSeeCarriers || canSeeInvoices || canSeeContracts;

  // A Verificação (KYC) é a única tela do Repasses que depende do PROVEDOR:
  // ela é a conta Asaas da marca. O gate é ter CREDENCIAL Asaas (SRV-133), não
  // ter o Asaas como provedor padrão — marca em migração Zoop → Asaas tem a
  // coluna legada em 'zoop' e é justamente quem precisa aprovar o KYC.
  // `enabled` segura o request até sabermos que é brand_user: `/settings` é só
  // pra eles, e é a mesma query que as telas do Repasses já usam.
  const repassesSettings = useRepassesSettings({ enabled: canSeeRepasses });
  const canSeeKyc = hasAsaasCredential(repassesSettings.data);
  const repassesSection = data.navRepasses
    ? {
        ...data.navRepasses,
        items: data.navRepasses.items.map((item) =>
          item.url === '/repasses/kyc' ? { ...item, show: canSeeKyc } : item,
        ),
      }
    : undefined;

  // Cada item da Auditoria é gateado pela sua flag; o Glossário (referência
  // estática) aparece se qualquer área da Auditoria estiver visível.
  const auditoriaSection = data.navAuditoria
    ? {
        ...data.navAuditoria,
        items: data.navAuditoria.items.map((item) => {
          let show = canSeeAuditoria;
          if (item.url === '/auditoria/transportadoras') show = canSeeCarriers;
          else if (item.url === '/auditoria/faturas') show = canSeeInvoices;
          else if (item.url === '/auditoria/contratos') show = canSeeContracts;
          return { ...item, show };
        }),
      }
    : undefined;

  // Schedule SupportInfoPopup to open only one day after Feedback dialog opens
  const { feedbackDialogOpen } = useStoreState(
    (state) => state.userFeedbackDialog,
  );
  const { setSupportInfoDialogShouldOpenAgainAt } = useStoreActions(
    (actions) => actions.supportInfoPopup,
  );
  const prevFeedbackOpenRef = React.useRef<boolean>(feedbackDialogOpen);
  React.useEffect(() => {
    if (feedbackDialogOpen && !prevFeedbackOpenRef.current) {
      setSupportInfoDialogShouldOpenAgainAt(
        addDays(new Date(), 1).toISOString(),
      );
    }
    prevFeedbackOpenRef.current = feedbackDialogOpen;
  }, [feedbackDialogOpen, setSupportInfoDialogShouldOpenAgainAt]);

  // deprecation warning of v1 pages
  const { setV2RoutesEnabled, setV2OrdersEnabled } = useStoreActions(
    (actions) => actions.preferences,
  );

  return (
    <React.Fragment>
      <DoubleSidebar {...props} className={cn('z-30', className)}>
        <DoubleSidebar.Header className="h-[var(--header-height)] border-b border-dashed">
          <DoubleSidebar.Menu>
            <DoubleSidebar.MenuItem>
              <div className="flex items-center justify-between gap-x-2">
                <DoubleSidebar.MenuButton asChild>
                  <Link to="/">
                    <Avatar className="border-border/50 aspect-square h-6 w-6 rounded-md border p-0.5">
                      <Avatar.Image src={sellerTheme.favicon} />
                    </Avatar>
                    <span className="font-medium">
                      {sellerTheme.carrier_name}
                    </span>
                  </Link>
                </DoubleSidebar.MenuButton>

                <div
                  className="hover:bg-muted flex cursor-pointer items-center rounded-md p-2"
                  onClick={() => signoff()}
                >
                  <LogOut className="h-4 w-4" />
                </div>
              </div>
            </DoubleSidebar.MenuItem>
          </DoubleSidebar.Menu>
        </DoubleSidebar.Header>
        {portalContexts.length ? (
          <ContextSwitcher
            activeProfile={activeProfile}
            activeContextId={activeSellerGroupId}
            isHomologation={isHomologation}
            isMultiEnv={isMultiEnv}
            contexts={portalContexts}
            hasHomologationContext={hasHomologationContext}
            portalUrl={process.env['NX_PORTAL_URL'] ?? ''}
            onProfileChange={(profile) => {
              const nextContext = portalMe?.seller_groups.find(
                (context) =>
                  context.profile === profile &&
                  context.is_homologation === isHomologation,
              );
              navigateToPortalContext(nextContext);
            }}
            onContextChange={(id) => {
              const nextContext = portalMe?.seller_groups.find(
                (context) => context.id === id,
              );
              navigateToPortalContext(nextContext);
            }}
            onHomologationToggle={(enabled) => {
              const nextContext = portalMe?.seller_groups.find(
                (context) =>
                  context.profile === activeProfile &&
                  context.is_homologation === enabled,
              );
              navigateToPortalContext(nextContext);
            }}
          />
        ) : null}
        <DoubleSidebar.Content className="gap-y-0">
          {isSettingsPage ? (
            <React.Fragment>
              <NavMain
                key={data.navSettingsGeneral?.title}
                section={data.navSettingsGeneral}
                defaultOpen={true}
              />
              <NavMain
                key={data.navSettingsDeveloper?.title}
                section={data.navSettingsDeveloper}
                defaultOpen={true}
              />
              <NavMain
                key={data.navSettingsOthers?.title}
                section={data.navSettingsOthers}
                defaultOpen={true}
              />
            </React.Fragment>
          ) : (
            <React.Fragment>
              {/* <NavMain
                key={data.navCatchUp?.title}
                section={data.navCatchUp}
                defaultOpen={true}
              /> */}
              <NavMain
                key={data.navOperation?.title}
                section={data.navOperation}
                defaultOpen={true}
              />
              {/* Care gateado por `seller_group.feature_config.care_visible`.
               * Marca com flag ativa vê o menu (paywall vive no drawer);
               * marca sem flag não vê nada — rollout staged. */}
              {data.navCare?.show && (
                <NavMain
                  key={data.navCare?.title}
                  section={data.navCare}
                  defaultOpen={true}
                />
              )}
              {/* Repasses (pay-into-hermes): submenu do operador, azul dentro
               * das páginas via .repasses-feature. SEGURANÇA: só brand_users
               * (pay.brand_users) veem — canSeeRepasses vem do pay-api. */}
              {canSeeRepasses && repassesSection?.show && (
                <NavMain
                  key={repassesSection.title}
                  section={repassesSection}
                  defaultOpen={true}
                />
              )}
              {/* Auditoria: segregado do Repasses. Gate por flag: cada item
               * some sem a sua FF (carriers/invoices/contracts). */}
              {canSeeAuditoria && auditoriaSection && (
                <NavMain
                  key="pay-auditoria"
                  section={auditoriaSection}
                  defaultOpen={true}
                />
              )}
              {/* NavMain não checa section.show (só item.show) e renderiza o
                * título sempre — guardamos aqui pra respeitar section.show. */}
              {data.navSupport?.show && (
                <NavMain
                  key={data.navSupport?.title}
                  section={data.navSupport}
                  defaultOpen={true}
                />
              )}
              {data.navEngage?.show && (
                <NavMain
                  key={data.navEngage?.title}
                  section={data.navEngage}
                  defaultOpen={true}
                />
              )}
              <NavMain
                key={data.navQuotation?.title}
                section={data.navQuotation}
                defaultOpen={false}
              />
              {/* Página nativa do LOG (/payments). Renomeada pra "Faturas" pra
               * não colidir com a "Auditoria" do pay-into-hermes — as duas podem
               * coexistir pro brand_user (Faturas = nativa; Auditoria = nossa). */}
              {isPaymentsEnabled && (
                <NavMain
                  key="log-native-audit"
                  section={data.navAudit}
                  defaultOpen={false}
                />
              )}
              <NavMain
                key={data.navSetup?.title}
                section={data.navSetup}
                defaultOpen={false}
              />
            </React.Fragment>
          )}
        </DoubleSidebar.Content>
        <DoubleSidebar.Footer>
          <DoubleSidebar.Menu>
            <ChangelogWidget />
            {!isSettingsPage ? (
              <React.Fragment>
                <DoubleSidebar.GroupLabel>
                  Você está visualizando:
                </DoubleSidebar.GroupLabel>
                <DoubleSidebar.MenuItem>
                  <Button
                    variant="outline"
                    onClick={() => setOpenSelectSellersDialog(true)}
                    size="tight"
                    className="w-full justify-between gap-x-2"
                    disabled={isLoadingMe}
                  >
                    <span className="truncate">
                      {isLoadingMe
                        ? 'Carregando...'
                        : selectedSellers.length === sellers.length
                          ? `Todas as filiais (${sellers.length})`
                          : `${selectedSellers.length} / ${sellers.length} filiais`}
                    </span>
                    <span className="flex shrink-0 items-center gap-x-1">
                      <kbd className="bg-muted text-muted-foreground pointer-events-none flex select-none items-center rounded border px-1.5 font-sans text-xs">
                        {isMac ? '⌘' : 'Ctrl'}
                      </kbd>
                      <kbd className="bg-muted text-muted-foreground pointer-events-none flex aspect-square select-none items-center justify-center rounded border px-1.5 font-sans text-xs">
                        B
                      </kbd>
                    </span>
                  </Button>
                </DoubleSidebar.MenuItem>
              </React.Fragment>
            ) : null}

            <DoubleSidebar.MenuItem>
              <div className="flex items-center gap-x-1">
                <SecurityUserChip
                  name={portalMe?.user?.name}
                  email={portalMe?.user?.email || me?.email}
                  className="flex-1"
                />

                <div
                  className="hover:bg-muted flex shrink-0 cursor-pointer items-center rounded-md p-2"
                  onClick={() => {
                    navigate('/support/tickets');
                    productTracker.track('Open Support Widget');
                  }}
                >
                  <HelpCircle className="h-5 w-5" />
                </div>

                {user?.type !== UserTypeEnum.USER &&
                  (isSettingsPage ? (
                    <div
                      className="hover:bg-muted flex shrink-0 cursor-pointer items-center rounded-md p-2"
                      onClick={() => navigate('/')}
                    >
                      <House className="h-5 w-5" />
                    </div>
                  ) : (
                    <div
                      className="hover:bg-muted flex shrink-0 cursor-pointer items-center rounded-md p-2"
                      onClick={() => navigate('/settings')}
                    >
                      <Settings className="h-5 w-5" />
                    </div>
                  ))}
              </div>
            </DoubleSidebar.MenuItem>
          </DoubleSidebar.Menu>
        </DoubleSidebar.Footer>
        <DoubleSidebar.Rail />
      </DoubleSidebar>

      <SelectSellersDialog
        open={openSelectSellersDialog}
        onOpenChange={setOpenSelectSellersDialog}
        sellers={sellers}
        defaultValue={selectedSellersForDialog}
        onSelect={handleSelectSellers}
        sellersIDontHaveAccess={sellersIDontHaveAccess}
        minimumAllowedSelectedSellers={1}
        useAllSellers={useAllSellers}
        onUseAllSellersChange={setUseAllSellers}
      />
      <SupportInfoPopup />
    </React.Fragment>
  );
}

export function NavMain({
  section,
  defaultOpen = true,
}: {
  section?: {
    title: string;
    items: {
      title: string;
      url: string;
      icon: LucideIcon;
      disabled?: boolean;
      badge?: React.ReactNode;
      isActive?: boolean;
      show?: boolean;
      external?: boolean;
      items?: {
        title: string;
        url: string;
      }[];
    }[];
  };
  defaultOpen?: boolean;
}) {
  if (!section) return null;

  const [isOpen, setIsOpen] = useLocalStorage<boolean>(
    `sidebar-section-${section.title.toLowerCase().replace(/\s+/g, '-')}`,
    defaultOpen,
  );

  return (
    <DoubleSidebar.Group>
      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className="group/collapsible"
      >
        {section.title && (
          <Collapsible.Trigger asChild>
            <DoubleSidebar.GroupLabel className="hover:bg-sidebar-accent cursor-default select-none">
              {section.title}
              <Triangle className="ml-2 h-2 w-2 fill-current group-data-[state=closed]/collapsible:rotate-90 group-data-[state=open]/collapsible:rotate-180" />
            </DoubleSidebar.GroupLabel>
          </Collapsible.Trigger>
        )}
        <Collapsible.Content className="data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden">
          <DoubleSidebar.Menu>
            {section.items.map(
              (item) =>
                item.show && (
                  <Collapsible
                    key={item.title}
                    asChild
                    defaultOpen={item.isActive}
                  >
                    <DoubleSidebar.MenuItem>
                      <DoubleSidebar.MenuButton
                        asChild
                        tooltip={{ children: item.title, hidden: false }}
                        className={cn(
                          item.disabled && 'pointer-events-none opacity-50',
                        )}
                      >
                        {item.external ? (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <item.icon className="h-4 w-4 shrink-0" />
                            <span className="truncate">{item.title}</span>
                            {item.badge}
                            <ArrowUpRight className="text-muted-foreground ml-auto h-3.5 w-3.5 shrink-0" />
                          </a>
                        ) : (
                          <Link to={item.url}>
                            <item.icon className="h-4 w-4 shrink-0" />
                            <span className="truncate">{item.title}</span>
                            {item.badge}
                          </Link>
                        )}
                      </DoubleSidebar.MenuButton>
                      {item.items?.length ? (
                        <>
                          <Collapsible.Trigger asChild>
                            <DoubleSidebar.MenuAction className="data-[state=open]:rotate-90">
                              <ChevronRight className="h-4 w-4" />
                              <span className="sr-only">Toggle</span>
                            </DoubleSidebar.MenuAction>
                          </Collapsible.Trigger>
                          <Collapsible.Content>
                            <DoubleSidebar.MenuSub>
                              {item.items?.map((subItem) => (
                                <DoubleSidebar.MenuSubItem key={subItem.title}>
                                  <DoubleSidebar.MenuSubButton asChild>
                                    <Link to={subItem.url}>
                                      <span>{subItem.title}</span>
                                    </Link>
                                  </DoubleSidebar.MenuSubButton>
                                </DoubleSidebar.MenuSubItem>
                              ))}
                            </DoubleSidebar.MenuSub>
                          </Collapsible.Content>
                        </>
                      ) : null}
                    </DoubleSidebar.MenuItem>
                  </Collapsible>
                ),
            )}
          </DoubleSidebar.Menu>
        </Collapsible.Content>
      </Collapsible>
    </DoubleSidebar.Group>
  );
}

export const HelpDropdown = () => {
  const navigate = useNavigate();

  const helpData = [
    {
      title: 'Suporte',
      url: undefined,
      external: false,
      icon: MessageSquareText,
      onClick: () => {
        navigate('/support/tickets');
        productTracker.track('Open Support Widget');
      },
    },
    {
      title: 'Documentação API',
      url: 'https://docs.abbiamolog.com/',
      external: true,
      disabled: true,
      badge: 'Em breve',
      icon: BookText,
      onClick: () => {
        window.open('https://docs.abbiamolog.com/', '_blank');
      },
    },
    {
      title: 'Abbiamo Status',
      url: 'https://status.abbiamolog.com/',
      external: true,
      disabled: true,
      badge: 'Em breve',
      icon: CircleCheck,
      onClick: () => {
        window.open('https://status.abbiamolog.com/', '_blank');
      },
    },
    {
      title: 'Changelog',
      url: '/changelog',
      external: false,
      disabled: true,
      badge: 'Em breve',
      icon: PanelTop,
      onClick: () => {
        window.open('/changelog', '_blank');
      },
    },
  ];

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <div className="hover:bg-muted flex cursor-pointer items-center rounded-md p-2">
          <HelpCircle className="h-5 w-5" />
        </div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="start">
        {helpData.map((item) => (
          <DropdownMenu.Item
            key={item.title}
            onClick={item.onClick}
            disabled={item.disabled}
          >
            <div className="flex items-center gap-x-2">
              <item.icon className="h-4 w-4" />
              {item.title}
              {item.badge && (
                <Badge variant="outline" className="ml-auto">
                  {item.badge}
                </Badge>
              )}
            </div>
            {item.external && <ArrowUpRight className="h-4 w-4" />}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};
